/**
 * Meaning of Nara orb: gold loading sphere first, then app mood
 * prototypes, then gold again. Crossfade between states.
 * Reference: Nara-App docs/REFERENCE/prototypes/moods/
 */
const HOME_MOOD = 'DEFAULT';
const HOME_CYCLE_MOODS = Object.freeze([
  'CALM',
  'ENERGY',
  'SUCCESS',
  'STRICT',
  'MINDSET',
]);
const MOOD_RGB = {
  DEFAULT: [211, 149, 66],
  CALM: [107, 157, 184],
  MEDITATION: [184, 164, 212],
  ENERGY: [255, 82, 0],
  SUCCESS: [255, 255, 255],
  STRICT: [224, 35, 35],
  HEALTH: [74, 145, 115],
  MINDSET: [139, 107, 168],
};
const IMPULSE_MS = 320;
const FADE_MS = 560;
const MOOD_LABELS = {
  CALM: { title: 'Calming', line: 'To help you feel calm' },
  ENERGY: { title: 'Motivating', line: 'To inspire you to keep going' },
  SUCCESS: { title: 'Celebratory', line: 'To celebrate your progress' },
  STRICT: { title: 'Firm', line: 'To keep you accountable' },
  MINDSET: { title: 'Meditate', line: 'To guide your meditation' },
};

class NaraMeaningOrb extends HTMLElement {
  connectedCallback() {
    if (this.booted) return;
    this.booted = true;
    this.atHome = true;
    this.guestIndex = 0;
    this.lastTapAt = 0;
    this.activeFrame = 0;
    this.layerZ = 2;
    this.revealSeq = 0;
    this.homeOrb = this.querySelector('nara-orb-loading');
    this.nameEl =
      this.querySelector('.nara-landing-meaning__mood-name') ||
      this.closest('.nara-landing-meaning__orb-col')?.querySelector('.nara-landing-meaning__mood-name');
    this.frames = [...this.querySelectorAll('iframe')];
    while (this.frames.length < 2) {
      const frame = document.createElement('iframe');
      frame.setAttribute('title', 'Nara mood');
      frame.setAttribute('tabindex', '-1');
      frame.setAttribute('aria-hidden', 'true');
      this.append(frame);
      this.frames.push(frame);
    }
    this.moodAbort = new AbortController();
    this.bindTap();
    this.showHome({ instant: true });
    this.watchIntroBloom();
  }

  disconnectedCallback() {
    this.moodAbort?.abort();
    this.bloomMo?.disconnect();
  }

  watchIntroBloom() {
    const shell = this.closest('.nara-landing-meaning__shell');
    if (!shell) return;
    const tryPlay = () => {
      if (this.introBloomed || !this.atHome) return;
      if (!shell.hasAttribute('data-inview')) return;
      this.introBloomed = true;
      const delay = this.reducedMotion() ? 0 : 220;
      window.setTimeout(() => {
        if (this.atHome) this.homeOrb?.playBloom?.();
      }, delay);
    };
    tryPlay();
    this.bloomMo = new MutationObserver(tryPlay);
    this.bloomMo.observe(shell, { attributes: true, attributeFilter: ['data-inview'] });
  }

  sequence() {
    const guests = window.NARA_ORB_MOODS?.NARA_HOME_CYCLE_MOODS || HOME_CYCLE_MOODS;
    const moods = [...guests].filter((id) => id !== HOME_MOOD);
    return [...moods, HOME_MOOD];
  }

  bindTap() {
    const { signal } = this.moodAbort;
    let startX = 0;
    let startY = 0;
    this.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
        startY = event.clientY;
      },
      { signal }
    );
    this.addEventListener(
      'pointerup',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (Math.hypot(dx, dy) > 12) return;
        this.cycleMoodTap();
      },
      { signal }
    );
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.cycleMoodTap();
      },
      { signal }
    );
  }

  cycleMoodTap() {
    const now = performance.now();
    if (now - this.lastTapAt < FADE_MS - 80) return;
    this.lastTapAt = now;
    const loop = this.sequence();
    const next = loop[this.guestIndex % loop.length] || HOME_MOOD;
    this.guestIndex = (this.guestIndex + 1) % loop.length;
    if (next === HOME_MOOD) this.showHome();
    else this.showMood(next);
    this.bounce();
  }

  reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  fadeMs() {
    return this.reducedMotion() ? 1 : FADE_MS;
  }

  layers() {
    return [this.homeOrb, ...this.frames].filter(Boolean);
  }

  revealLayer(incoming, { instant } = {}) {
    if (!incoming) return;
    this.layerZ += 1;
    incoming.style.zIndex = String(this.layerZ);
    const layers = this.layers();
    if (instant) {
      layers.forEach((layer) => {
        layer.style.transition = 'none';
      });
      incoming.classList.add('is-on');
      layers.forEach((layer) => {
        if (layer !== incoming) layer.classList.remove('is-on');
      });
      void incoming.offsetWidth;
      layers.forEach((layer) => {
        layer.style.transition = '';
      });
      return;
    }
    incoming.classList.add('is-on');
    layers.forEach((layer) => {
      if (layer !== incoming) layer.classList.remove('is-on');
    });
  }

  showHome({ instant } = {}) {
    this.revealSeq += 1;
    this.atHome = true;
    this.classList.remove('is-mood');
    this.setAttribute('data-mood', HOME_MOOD);
    this.syncHalo(HOME_MOOD);
    this.showMoodName(HOME_MOOD);
    if (!instant) this.homeOrb?.playBloom?.();
    this.revealLayer(this.homeOrb, { instant });
    window.setTimeout(() => {
      if (!this.atHome) return;
      this.frames.forEach((frame) => {
        frame.removeAttribute('src');
        frame.removeAttribute('srcdoc');
      });
    }, instant ? 0 : this.fadeMs());
  }

  showMood(id) {
    const mood = String(id || 'CALM').toUpperCase();
    const src = this.getAttribute(`data-${mood.toLowerCase()}`);
    if (!src) return;

    const incoming = this.frames[1 - this.activeFrame];
    const outgoing = this.frames[this.activeFrame];
    this.revealSeq += 1;
    const seq = this.revealSeq;
    const reveal = () => {
      if (seq !== this.revealSeq) return;
      this.atHome = false;
      this.classList.add('is-mood');
      this.setAttribute('data-mood', mood);
      this.syncHalo(mood);
      this.revealLayer(incoming);
      this.showMoodName(mood);
      this.activeFrame = 1 - this.activeFrame;
      window.setTimeout(() => {
        if (outgoing !== this.frames[this.activeFrame]) {
          outgoing.removeAttribute('src');
          outgoing.removeAttribute('srcdoc');
        }
      }, this.fadeMs());
    };

    incoming.addEventListener('load', reveal, { once: true });
    if (incoming.getAttribute('src') === src) reveal();
    else incoming.src = src;
  }

  showMoodName(moodId) {
    if (!this.nameEl) return;
    this.nameEl.classList.remove('is-play');
    this.nameEl.replaceChildren();
    const label = MOOD_LABELS[moodId];
    if (!label) return;
    const title = document.createElement('strong');
    title.className = 'nara-landing-meaning__mood-title';
    title.textContent = label.title;
    const line = document.createElement('span');
    line.className = 'nara-landing-meaning__mood-line';
    line.textContent = label.line;
    this.nameEl.append(title, line);
    if (this.reducedMotion()) return;
    void this.nameEl.offsetWidth;
    this.nameEl.classList.add('is-play');
  }

  syncHalo(moodId) {
    const host = this.closest('.nara-landing-meaning__orb-col');
    const rgb = MOOD_RGB[moodId] || MOOD_RGB.DEFAULT;
    if (!host) return;
    host.style.setProperty('--nara-meaning-glow', `${rgb[0]} ${rgb[1]} ${rgb[2]}`);
    host.style.setProperty('--nara-meaning-glow-soft', `${rgb[0]} ${rgb[1]} ${rgb[2]}`);
  }

  bounce() {
    if (this.reducedMotion()) return;
    this.classList.remove('is-bounce');
    void this.offsetWidth;
    this.classList.add('is-bounce');
    window.setTimeout(() => this.classList.remove('is-bounce'), IMPULSE_MS + 40);
  }
}

if (!customElements.get('nara-meaning-orb')) {
  customElements.define('nara-meaning-orb', NaraMeaningOrb);
}
